import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, X } from 'lucide-react';

const MAX_S = 120;
const typWahl = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || '';
const endung = (t) => (t.includes('mp4') ? 'm4a' : t.includes('ogg') ? 'ogg' : 'webm');
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Sprachnachricht aufnehmen (max. 2 Min.) – Ergebnis wird als Anhang übergeben und normal gesendet.
export const SprachAufnahme = ({ onFertig, disabled, onLaeuft }) => {
  const [laeuft, setLaeuft] = useState(false);
  const [sek, setSek] = useState(0);
  const [fehler, setFehler] = useState('');
  const rec = useRef(null);
  const abbruch = useRef(false);
  const timer = useRef(null);
  const stopp = (verwerfen = false) => { abbruch.current = verwerfen; rec.current?.state === 'recording' && rec.current.stop(); };
  useEffect(() => () => { clearInterval(timer.current); abbruch.current = true; rec.current?.state === 'recording' && rec.current.stop(); }, []);
  useEffect(() => { if (laeuft && sek >= MAX_S) stopp(); }, [sek, laeuft]);
  useEffect(() => { onLaeuft?.(laeuft); }, [laeuft, onLaeuft]);
  const start = async () => {
    setFehler('');
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { setFehler('Aufnahme wird von diesem Browser nicht unterstützt'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = typWahl();
      const r = new MediaRecorder(stream, typ ? { mimeType: typ, audioBitsPerSecond: 32000 } : undefined);
      const teile = [];
      r.ondataavailable = (e) => e.data.size && teile.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current); setLaeuft(false);
        if (abbruch.current || !teile.length) return;
        const mime = (r.mimeType || typ || 'audio/webm').split(';')[0];
        const jetzt = new Date();
        const name = `Sprachnachricht_${jetzt.toLocaleDateString('sv-SE')}_${String(jetzt.getHours()).padStart(2, '0')}-${String(jetzt.getMinutes()).padStart(2, '0')}.${endung(mime)}`;
        onFertig(new File(teile, name, { type: mime }));
      };
      rec.current = r; abbruch.current = false;
      r.start(1000); setSek(0); setLaeuft(true);
      timer.current = setInterval(() => setSek((s) => s + 1), 1000);
    } catch { setFehler('Kein Zugriff auf das Mikrofon'); }
  };
  if (laeuft) return (
    <div className="flex-1 sm:flex-none min-w-0 flex items-center gap-2 h-9 px-3 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[12px] font-semibold whitespace-nowrap" data-testid="sprach-aufnahme-laeuft">
      <span className="w-2 h-2 rounded-full bg-rose-600 animate-pulse" />Aufnahme {mmss(sek)} / {mmss(MAX_S)}
      <button type="button" onClick={() => stopp(true)} aria-label="Aufnahme verwerfen" data-tipp="Verwerfen" className="ml-auto w-7 h-7 inline-flex items-center justify-center rounded-full hover:bg-rose-100" data-testid="sprach-verwerfen"><X className="w-4 h-4" /></button>
      <button type="button" onClick={() => stopp(false)} aria-label="Aufnahme beenden" data-tipp="Beenden – danach senden" className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-rose-600 text-white hover:bg-rose-700" data-testid="sprach-stopp"><Square className="w-3 h-3" /></button>
    </div>
  );
  return (
    <>
      <button type="button" onClick={start} disabled={disabled} aria-label="Sprachnachricht aufnehmen" data-tipp={fehler || 'Sprachnachricht aufnehmen (max. 2 Min.)'} data-testid="sprach-start"
        className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 ${fehler ? 'text-rose-600' : 'text-[#34536b] hover:bg-[#f5f8fa]'}`}>
        <Mic className="w-4 h-4" />
      </button>
      {fehler && <span className="text-[11px] text-rose-700" role="alert" data-testid="sprach-fehler">{fehler}</span>}
    </>
  );
};

// Abspielen im Chat: lädt die Datei erst beim ersten Klick.
export const SprachWiedergabe = ({ att, onLoad, mine }) => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio controls autoPlay src={url} className="mt-1.5 w-[240px] max-w-full h-9" data-testid={`sprach-player-${att.gridfs_id}`} />;
  return (
    <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { setUrl(await onLoad(att)); } catch { /* bleibt als Download */ } setBusy(false); }}
      className={`mt-1.5 inline-flex items-center gap-2 h-9 px-3 rounded-full text-[12px] font-semibold transition-colors ${mine ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-[#1B3A5C]/10 text-[#1B3A5C] hover:bg-[#1B3A5C]/15'}`}
      data-testid={`sprach-abspielen-${att.gridfs_id}`}>
      <Mic className="w-4 h-4" />{busy ? 'Lädt …' : 'Sprachnachricht abspielen'}
    </button>
  );
};
